<script setup lang="ts">
import type { MegamenuNavItem, MegamenuSection } from '~/data/megamenu'
import {
  megamenuSalesPath,
  megamenuSectionShowsInSidebar,
} from '~/data/megamenu'
import IconChevronRight from '~/assets/icons/chevron-right.svg?component'
import IconLightning from '~/assets/icons/lightning-charge-fill.svg?component'
import { sendMenuClick } from '~/composables/useGtmEvents'
import { isExternalUrl } from '~/utils/urlHelpers'

const props = defineProps<{
  item: MegamenuNavItem
  selectedSectionIndex: number
}>()

const emit = defineEmits<{
  (e: 'selectSection', index: number): void
}>()

const { baseUrl } = useRuntimeConfig().public as { baseUrl: string }
const resolveHref = useFullLinkUrl()
const isExternal = (href: string) => isExternalUrl(href, baseUrl)

const sectionsWithContent = computed(() =>
  (props.item.sections ?? []).filter(megamenuSectionShowsInSidebar)
)

function isSalesSection(section: MegamenuSection) {
  return section.isPromotionSalesLink === true
}

function handlePrimaryLinkClick() {
  if (props.item.primaryLink) {
    sendMenuClick({
      menuName: props.item.label,
      itemLabel: props.item.primaryLink.label,
      itemPath: props.item.primaryLink.href,
    })
  }
}

function handleSalesClick(section: MegamenuSection) {
  sendMenuClick({
    menuName: props.item.label,
    sectionTitle: section.title,
    itemLabel: section.title,
    itemPath: megamenuSalesPath,
  })
}

function handleSectionPrimaryLinkClick(section: MegamenuSection) {
  const link = section.primaryLink
  if (!link?.href) return
  sendMenuClick({
    menuName: props.item.label,
    sectionTitle: section.title,
    itemLabel: link.label,
    itemPath: link.href,
  })
}
</script>

<template>
  <div
    data-test-id="megamenu-dropdown-nav"
    class="flex flex-[0_0_25%] flex-col min-w-48 border-r border-gray-200 pr-6"
  >
    <!-- Primary "All X" link -->
    <div
      v-if="item.primaryLink"
      data-test-id="megamenu-dropdown-nav-primary-link"
      class="pt-3 border-gray-100"
    >
      <a
        v-if="isExternal(item.primaryLink.href)"
        :href="resolveHref(item.primaryLink.href)"
        class="flex items-center gap-2 py-1.5 text-sm text-stone-800 no-underline hover:text-teal-600"
        target="_blank"
        rel="noopener"
        @click="handlePrimaryLinkClick"
      >
        {{ item.primaryLink.label }}
        <MegamenuPersonalizeBadge v-if="item.primaryLink.hasPersonalizeBadge" />
      </a>
      <a
        v-else
        :href="resolveHref(item.primaryLink.href)"
        class="flex items-center gap-2 py-1.5 text-sm text-stone-900 no-underline hover:text-teal-600"
        @click="handlePrimaryLinkClick"
      >
        {{ item.primaryLink.label }}
        <MegamenuPersonalizeBadge v-if="item.primaryLink.hasPersonalizeBadge" />
      </a>
    </div>
    <!-- Section list -->
    <nav
      data-test-id="megamenu-dropdown-nav-sections"
      class="flex flex-col pt-2"
    >
      <template
        v-for="(section, index) in sectionsWithContent"
        :key="section.title"
      >
        <!-- Sales: pill style with lightning icon -->
        <a
          v-if="isSalesSection(section)"
          :data-test-id="`megamenu-dropdown-nav-section-${index}-sales`"
          :href="resolveHref(megamenuSalesPath)"
          class="inline-flex items-center rounded-lg bg-orange-50 px-2 py-3 -mx-2 gap-1.5 text-sm font-medium text-red-500 no-underline hover:bg-amber-200"
          @click="handleSalesClick(section)"
        >
          <IconLightning class="h-4 w-4 shrink-0 text-red-500" />
          {{ section.title }}
        </a>
        <!-- Direct-link section row -->
        <a
          v-else-if="
            section.primaryLink?.href && isExternal(section.primaryLink.href)
          "
          :data-test-id="`megamenu-dropdown-nav-section-${index}-primary`"
          :href="resolveHref(section.primaryLink.href)"
          class="flex items-center gap-2 rounded-lg px-2 py-3 -mx-2 text-sm text-stone-800 no-underline hover:bg-gray-50 hover:text-teal-800"
          target="_blank"
          rel="noopener"
          @click="handleSectionPrimaryLinkClick(section)"
        >
          {{ section.primaryLink.label }}
          <MegamenuPersonalizeBadge
            v-if="section.primaryLink.hasPersonalizeBadge"
          />
        </a>
        <a
          v-else-if="section.primaryLink?.href"
          :data-test-id="`megamenu-dropdown-nav-section-${index}-primary`"
          :href="resolveHref(section.primaryLink.href)"
          class="flex items-center gap-2 rounded-lg px-2 py-3 -mx-2 text-sm text-stone-900 no-underline hover:bg-gray-50 hover:text-teal-800"
          @click="handleSectionPrimaryLinkClick(section)"
        >
          {{ section.primaryLink.label }}
          <MegamenuPersonalizeBadge
            v-if="section.primaryLink.hasPersonalizeBadge"
          />
        </a>
        <!-- Regular section row -->
        <button
          v-else
          :data-test-id="`megamenu-dropdown-nav-section-${index}`"
          type="button"
          class="flex items-center justify-between hover:bg-[rgba(93,206,241,0.10)] rounded-lg px-2 py-3 -mx-2 text-left text-sm transition-colors"
          :class="
            selectedSectionIndex === index
              ? 'bg-[rgba(93,206,241,0.10)] text-teal-800'
              : 'text-stone-800 hover:bg-gray-50 hover:text-teal-800'
          "
          @click="emit('selectSection', index)"
          @mouseenter="emit('selectSection', index)"
        >
          <span>{{ section.title }}</span>
          <IconChevronRight class="h-4 w-4 shrink-0" />
        </button>
      </template>
    </nav>
  </div>
</template>
